import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import React, { useState } from 'react';
import { ActivityIndicator, Image, Pressable, StyleSheet, Text, View } from 'react-native';

import { Child } from '../../interface/Child';
import { getAgeFromDOB } from '../../utils/childUtils';

type Props = {
    child: Child;
    onEdit: (child: Child) => void;
    onDelete: () => void;
    isSaving?: boolean;
};

const Chip: React.FC<{ label: string; variant?: 'allergy' | 'interest' }> = ({ label, variant = 'interest' }) => (
    <View style={[chipStyles.chip, variant === 'allergy' ? chipStyles.allergyChip : chipStyles.interestChip]}>
        <Text style={[chipStyles.label, variant === 'allergy' ? chipStyles.allergyLabel : chipStyles.interestLabel]}>
            {label}
        </Text>
    </View>
);

const chipStyles = StyleSheet.create({
    chip: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 20,
        marginRight: 4,
        marginBottom: 4,
    },
    label: {
        fontSize: 11,
        fontWeight: '600',
    },
    interestChip: {
        backgroundColor: '#EFF6FF',
    },
    interestLabel: {
        color: '#1D4ED8',
    },
    allergyChip: {
        backgroundColor: '#FEF2F2',
    },
    allergyLabel: {
        color: '#B91C1C',
    },
});

const ChildCard: React.FC<Props> = ({ child, onEdit, onDelete, isSaving = false }) => {
    const [hovered, setHovered] = useState(false);
    const age = getAgeFromDOB(child.dateOfBirth);
    const hasAllergies = Array.isArray(child.allergies) && child.allergies.length > 0;
    const hasInterests = Array.isArray(child.interests) && child.interests.length > 0;
    const hasMedical = !!child.medicalNotes;
    const hasDetails = hasAllergies || hasInterests || hasMedical;

    return (
        <Pressable
            style={[styles.card, hovered && styles.cardHovered]}
            onPress={() => onEdit(child)}
            onHoverIn={() => setHovered(true)}
            onHoverOut={() => setHovered(false)}
        >
            {/* Header row */}
            <View style={styles.header}>
                {/* Avatar */}
                <View style={styles.avatarWrap}>
                    {child.profileImage ? (
                        <Image source={{ uri: String(child.profileImage) }} style={styles.avatar} resizeMode="cover" />
                    ) : (
                        <View style={styles.avatarPlaceholder}>
                            <Text style={styles.avatarInitials}>
                                {(child.firstName || '').charAt(0)}{(child.lastName || '').charAt(0)}
                            </Text>
                        </View>
                    )}
                </View>

                {/* Name + age */}
                <View style={styles.info}>
                    <Text style={styles.name}>{child.firstName} {child.lastName}</Text>
                    <View style={styles.agePill}>
                        <Text style={styles.ageText}>{age} yrs old</Text>
                    </View>
                </View>

                {/* Actions */}
                <View style={styles.actions}>
                    <Pressable
                        style={styles.editBtn}
                        onPress={() => onEdit(child)}
                    >
                        <MaterialCommunityIcons name="pencil-outline" size={16} color="#3C8248" />
                    </Pressable>
                    <Pressable
                        style={styles.deleteBtn}
                        onPress={(e: any) => { e.stopPropagation?.(); onDelete(); }}
                        disabled={isSaving}
                    >
                        {isSaving ? (
                            <ActivityIndicator size="small" color="#EF4444" />
                        ) : (
                            <MaterialCommunityIcons name="trash-can-outline" size={16} color="#EF4444" />
                        )}
                    </Pressable>
                </View>
            </View>

            {/* Detail chips */}
            {hasDetails && (
                <View style={styles.detailsSection}>
                    {hasAllergies && (
                        <View style={styles.detailRow}>
                            <MaterialCommunityIcons name="alert-circle-outline" size={13} color="#B91C1C" style={styles.detailIcon} />
                            <View style={styles.chipWrap}>
                                {child.allergies!.slice(0, 4).map((a, i) => (
                                    <Chip key={i} label={a} variant="allergy" />
                                ))}
                                {child.allergies!.length > 4 && (
                                    <Text style={styles.overflow}>+{child.allergies!.length - 4}</Text>
                                )}
                            </View>
                        </View>
                    )}
                    {hasInterests && (
                        <View style={styles.detailRow}>
                            <MaterialCommunityIcons name="star-outline" size={13} color="#1D4ED8" style={styles.detailIcon} />
                            <View style={styles.chipWrap}>
                                {child.interests!.slice(0, 4).map((i, idx) => (
                                    <Chip key={idx} label={i} variant="interest" />
                                ))}
                                {child.interests!.length > 4 && (
                                    <Text style={styles.overflow}>+{child.interests!.length - 4}</Text>
                                )}
                            </View>
                        </View>
                    )}
                    {hasMedical && (
                        <View style={styles.detailRow}>
                            <MaterialCommunityIcons name="medical-bag" size={13} color="#6B7280" style={styles.detailIcon} />
                            <Text style={styles.medicalText} numberOfLines={2}>{child.medicalNotes}</Text>
                        </View>
                    )}
                </View>
            )}

            {/* Edit hint */}
            <View style={styles.footer}>
                <MaterialCommunityIcons name="pencil" size={11} color="#94A3B8" />
                <Text style={styles.editHint}>Tap to edit</Text>
            </View>
        </Pressable>
    );
};

const styles = StyleSheet.create({
    card: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        marginBottom: 12,
        overflow: 'hidden',
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 8,
        elevation: 2,
    },
    cardHovered: {
        borderColor: '#3C8248',
        shadowOpacity: 0.1,
        shadowRadius: 12,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        gap: 12,
    },
    avatarWrap: {
        flexShrink: 0,
    },
    avatar: {
        width: 52,
        height: 52,
        borderRadius: 26,
        borderWidth: 2,
        borderColor: '#E2E8F0',
    },
    avatarPlaceholder: {
        width: 52,
        height: 52,
        borderRadius: 26,
        backgroundColor: '#3C8248',
        alignItems: 'center',
        justifyContent: 'center',
    },
    avatarInitials: {
        color: '#FFFFFF',
        fontWeight: '700',
        fontSize: 18,
        letterSpacing: 0.5,
    },
    info: {
        flex: 1,
        gap: 5,
    },
    name: {
        fontSize: 15,
        fontWeight: '700',
        color: '#0F172A',
        letterSpacing: -0.2,
    },
    agePill: {
        alignSelf: 'flex-start',
        backgroundColor: '#F1F5F9',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 20,
    },
    ageText: {
        fontSize: 11,
        fontWeight: '600',
        color: '#64748B',
    },
    actions: {
        flexDirection: 'row',
        gap: 4,
        alignItems: 'center',
    },
    editBtn: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: '#F0FDF4',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: '#DCFCE7',
    },
    deleteBtn: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: '#FEF2F2',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: '#FECACA',
    },
    // Details
    detailsSection: {
        paddingHorizontal: 16,
        paddingBottom: 12,
        gap: 6,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 6,
    },
    detailIcon: {
        marginTop: 4,
        flexShrink: 0,
    },
    chipWrap: {
        flex: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
    },
    overflow: {
        fontSize: 11,
        color: '#9CA3AF',
        fontWeight: '600',
        alignSelf: 'center',
        marginLeft: 2,
    },
    medicalText: {
        flex: 1,
        fontSize: 12,
        color: '#4B5563',
        lineHeight: 18,
        paddingTop: 2,
    },
    // Footer
    footer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        paddingVertical: 8,
        borderTopWidth: 1,
        borderTopColor: '#F8FAFC',
        backgroundColor: '#FAFBFC',
    },
    editHint: {
        fontSize: 11,
        color: '#94A3B8',
        fontWeight: '500',
    },
});

export default ChildCard;
